import type { DailyAsset } from '../domain/dailyEngine'

interface GuidedAssetContentProps {
  asset: DailyAsset
}

const formatLabels: Record<string, string> = {
  micro_action: 'Small action',
  recognition_story: 'Story for recognition',
  guided_reflection: 'Guided reflection',
  learning_card: 'Learn and notice',
  relationship_action: 'Relationship action',
  fatherhood_action: 'Fatherhood action',
  practical_action: 'Practical action',
}

export function GuidedAssetContent({ asset }: GuidedAssetContentProps) {
  const hasGuidance = Boolean(
    asset.plainLanguageExplanation
    || asset.recognitionStory
    || asset.realLifeExamples?.length
    || asset.exampleAnswers?.length
    || asset.lighterAlternative,
  )

  if (!hasGuidance) return null

  return (
    <div className="guided-asset-content">
      <div className="asset-access-row">
        {asset.assetFormat && <span>{formatLabels[asset.assetFormat] ?? asset.assetFormat.replaceAll('_', ' ')}</span>}
        {asset.cognitiveDemand && <span>Thinking: {asset.cognitiveDemand.replaceAll('_', ' ')}</span>}
        {asset.emotionalDemand && <span>Emotional load: {asset.emotionalDemand.replaceAll('_', ' ')}</span>}
      </div>

      {asset.plainLanguageExplanation && (
        <section className="plain-language-guide">
          <strong>What this means in everyday life</strong>
          <p>{asset.plainLanguageExplanation}</p>
        </section>
      )}

      {asset.recognitionStory && (
        <section className="recognition-story">
          <span>A composite story</span>
          <h3>See whether any part feels familiar</h3>
          <p>{asset.recognitionStory}</p>
          <small>This is an example, not a claim about you. It is completely valid if it does not fit.</small>
        </section>
      )}

      {asset.realLifeExamples?.length ? (
        <section className="real-life-examples">
          <strong>How this might show up</strong>
          <ul>{asset.realLifeExamples.map((example) => <li key={example}>{example}</li>)}</ul>
        </section>
      ) : null}

      {asset.exampleAnswers?.length ? (
        <details className="example-answers">
          <summary>Read three honest example answers</summary>
          <p>Use these to recognise what may be true-not as answers to copy.</p>
          <ol>{asset.exampleAnswers.map((answer, index) => <li key={answer}><span>{index === 0 ? 'Just beginning' : index === 1 ? 'Starting to notice' : 'More practised'}</span><p>{answer}</p></li>)}</ol>
        </details>
      ) : null}

      {asset.lighterAlternative && (
        <section className="lighter-alternative">
          <strong>If this feels like too much today</strong>
          <p>{asset.lighterAlternative}</p>
        </section>
      )}
    </div>
  )
}
